import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import {
  cancelTerminalCharge,
  getById as getOrderById,
  type TerminalCardType,
} from "../../services/orderService";

type TerminalPaymentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orderId: number | null;
  cardType?: TerminalCardType | string | null;
  onPaymentConfirmed?: () => void;
  onCancelled?: () => void;
};

const POLL_INTERVAL_MS = 3000;

const TerminalPaymentModal = ({
  isOpen,
  onClose,
  orderId,
  cardType = null,
  onPaymentConfirmed,
  onCancelled,
}: TerminalPaymentModalProps) => {
  const { t } = useTranslation();
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [polling, setPolling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const confirmedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setPaymentConfirmed(false);
      setPolling(false);
      setCancelling(false);
      confirmedRef.current = false;
      return;
    }

    if (orderId === null) {
      return;
    }

    let cancelled = false;
    setPolling(true);

    const pollStatus = async () => {
      try {
        const response = await getOrderById(orderId);

        if (!cancelled && !confirmedRef.current && response.data.status === "paid") {
          confirmedRef.current = true;
          setPaymentConfirmed(true);
          setPolling(false);
          toast.success(t("studio.orders.checkout.payment.terminal.paymentConfirmedToast"));
          onPaymentConfirmed?.();
        }
      } catch {
        // Keep polling; transient network errors should not close the wait modal.
      }
    };

    void pollStatus();
    const intervalId = window.setInterval(() => {
      void pollStatus();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isOpen, orderId, onPaymentConfirmed, t]);

  const handleCancelWait = async () => {
    if (orderId === null || cancelling || paymentConfirmed) {
      return;
    }

    setCancelling(true);

    try {
      await cancelTerminalCharge(orderId);
      toast.info(t("studio.orders.checkout.payment.terminal.cancelledToast"));
      onCancelled?.();
      onClose();
    } catch (error: any) {
      toast.error(
        typeof error?.data === "string" && error.data.length > 0
          ? error.data
          : t("studio.orders.checkout.payment.terminal.cancelError")
      );
    } finally {
      setCancelling(false);
    }
  };

  const cardTypeLabel =
    cardType === "credit"
      ? t("studio.orders.checkout.payment.terminal.cardType.credit")
      : cardType === "debit"
        ? t("studio.orders.checkout.payment.terminal.cardType.debit")
        : null;

  return (
    <Modal isOpen={isOpen} toggle={paymentConfirmed ? onClose : undefined} centered>
      <ModalHeader toggle={paymentConfirmed ? onClose : undefined}>
        {t("studio.orders.checkout.payment.terminal.title")}
      </ModalHeader>
      <ModalBody>
        {paymentConfirmed ? (
          <Alert color="success" className="mb-0">
            {t("studio.orders.checkout.payment.terminal.success")}
          </Alert>
        ) : (
          <>
            <div className="d-flex align-items-center gap-3 mb-3">
              {polling ? <Spinner size="sm" color="primary" /> : null}
              <div>
                <p className="mb-1 fw-medium">
                  {t("studio.orders.checkout.payment.terminal.waiting")}
                </p>
                <p className="mb-0 text-muted small">
                  {t("studio.orders.checkout.payment.terminal.hint")}
                </p>
              </div>
            </div>
            {cardTypeLabel ? (
              <Alert color="info" className="mb-0">
                {t("studio.orders.checkout.payment.terminal.cardTypeHint", {
                  type: cardTypeLabel,
                })}
              </Alert>
            ) : null}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {paymentConfirmed ? (
          <Button color="primary" onClick={onClose}>
            {t("studio.orders.checkout.payment.terminal.close")}
          </Button>
        ) : (
          <Button
            color="danger"
            outline
            onClick={() => {
              void handleCancelWait();
            }}
            disabled={cancelling}
          >
            {cancelling ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t("studio.orders.checkout.payment.terminal.cancelling")}
              </>
            ) : (
              t("studio.orders.checkout.payment.terminal.cancelWait")
            )}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default TerminalPaymentModal;
